import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { mediaRadiusField, universalFields } from '../_shared/universal.js';
/**
 * image-rail — la « bande d'images et de vidéos » des grandes marques (référence : la section « Meet
 * CloudTec » de on.com, capturée par la fondatrice le 14/09) : un panneau d'INTRO à gauche (grand visuel,
 * titre, phrase, bouton) et, à côté, une BANDE de cartes hautes qui défile à l'horizontale, chacune
 * portant une phrase courte en surimpression. Une carte sans média devient un aplat de couleur — c'est la
 * carte noire de la référence.
 *
 * CHAQUE EMPLACEMENT DE MÉDIA PREND UNE PHOTO **OU UN CLIP** (fondatrice, 19/09 : « on doit pouvoir mettre
 * aussi des vidéos »). Le panneau d'intro comme chaque carte porte donc un champ `video` à côté de son
 * `image` : le clip GAGNE quand les deux sont remplis, et la photo devient son affiche. Il joue comme une
 * photo animée — muet, en boucle, sans barre de lecture — parce que c'est ce que cette section est : une
 * suite de visuels, pas un lecteur. Aucun interrupteur de lecture, donc : un clip déposé bouge.
 *
 * Le `type` reste `image-rail` — le renommage est celui que la vendeuse LIT (`name`/`meta.label` et les
 * clés `secname.image-rail`), jamais l'adresse stockée dans les documents déjà publiés.
 *
 * La configuration tient en QUATRE réglages de mise en page (côté du panneau, largeur des cartes,
 * format, flèches) : c'est une section qu'on remplit de visuels, pas un tableau de bord. Le défilement
 * est du scroll-snap natif — sans JavaScript la bande se balaie déjà au doigt ; le moteur partagé
 * `sliderInit` ne fait qu'animer les flèches (voir Component.tsx).
 */
const CONTENT = 'Content';
const LAYOUT = 'Layout';
export const imageRailSchema = {
    type: 'image-rail',
    name: 'Image and video rail',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Content — le panneau d'intro ────────────────────────────────────────
        { id: 'image', type: 'image', label: 'Image', group: CONTENT },
        {
            id: 'video',
            type: 'video',
            label: 'Video',
            group: CONTENT,
            // Clip ENVOYÉ, jamais une adresse collée (règle du projet) : le contrôle ne prend qu'un envoi.
            info: 'Plays in place of the image above — silent, looping, with no playback bar. Use a short MP4/WebM rather than a GIF; the image is kept as its poster.',
        },
        playButtonField({
            defaultOn: false,
            group: CONTENT,
            visibleWhen: { field: 'video', empty: false },
        }),
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: CONTENT,
            default: 'Conçu dans le détail',
            defaultKey: 'section.image_rail_heading',
            aiWritable: true,
            maxLength: 60,
        },
        {
            id: 'text',
            type: 'text',
            label: 'Text',
            group: CONTENT,
            default: 'Regardez de plus près ce qui fait la différence.',
            defaultKey: 'section.image_rail_text',
            aiWritable: true,
            maxLength: 140,
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: CONTENT,
            default: 'En savoir plus',
            defaultKey: 'common.learn_more',
            maxLength: 32,
        },
        { id: 'buttonLink', type: 'link', label: 'Button link', group: CONTENT },
        // ── Layout — quatre réglages, pas un de plus ────────────────────────────
        {
            id: 'introPosition',
            type: 'select',
            label: 'Intro panel',
            group: LAYOUT,
            default: 'left',
            info: 'Which side the intro panel (image, heading, button) sits on. Choose None to show the rail alone, edge to edge.',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'none', label: 'None' },
            ],
        },
        {
            id: 'cardWidth',
            type: 'range',
            label: 'Card width',
            group: LAYOUT,
            min: 180,
            max: 480,
            step: 20,
            unit: 'px',
            default: 260,
            info: 'Width of one card on desktop. On phones the cards shrink on their own so the next one always peeks.',
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Card shape',
            group: LAYOUT,
            default: 'portrait',
            options: [
                { value: 'portrait', label: 'Portrait' },
                { value: 'square', label: 'Square' },
                { value: 'landscape', label: 'Landscape' },
            ],
        },
        {
            id: 'showArrows',
            type: 'checkbox',
            label: 'Show arrows',
            group: LAYOUT,
            default: true,
        },
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [
        {
            type: 'card',
            name: 'Card',
            max: 12,
            fields: [
                { id: 'image', type: 'image', label: 'Image' },
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    info: 'Plays in place of the card image — silent, looping, with no playback bar. The image is kept as its poster.',
                },
                // Pas de disque sur une carte qui est un LIEN : un bouton dans un `<a>` est une violation que
                // la porte `axe` rejette, et le clic doit rester celui de la carte. La case disparaît donc dès
                // qu'un lien est posé, plutôt que de rester là sans effet.
                playButtonField({
                    defaultOn: false,
                    visibleWhen: [
                        { field: 'video', empty: false },
                        { field: 'link', empty: true },
                    ],
                    info: 'Puts the round play/pause disc on this card’s clip. Unavailable while the card carries a link — a button inside a link would steal the card’s own click.',
                }),
                {
                    id: 'caption',
                    type: 'text',
                    label: 'Caption',
                    default: '',
                    aiWritable: true,
                    maxLength: 120,
                },
                {
                    id: 'backgroundColor',
                    type: 'color',
                    label: 'Background color',
                    default: { ref: 'custom', value: '#111111' },
                    info: 'Shown when the card has no image and no video — the caption then sits on this flat colour.',
                },
                {
                    id: 'textColor',
                    type: 'color',
                    label: 'Text color',
                    default: { ref: 'custom', value: '#FFFFFF' },
                },
                { id: 'link', type: 'link', label: 'Link' },
            ],
        },
    ],
    maxBlocks: 12,
};
export const imageRailPresets = [
    {
        name: 'Image and video rail',
        // `width: 'full'` — la bande doit courir jusqu'au bord, c'est tout le dessin de la référence.
        settings: {
            introPosition: 'left',
            cardWidth: 260,
            imageRatio: 'portrait',
            showArrows: true,
            colorScheme: 'light',
            width: 'full',
            paddingTop: 72,
            paddingBottom: 72,
        },
        blocks: [
            {
                type: 'card',
                settings: {
                    caption: '@t:section.image_rail_caption_1',
                    backgroundColor: { ref: 'custom', value: '#111111' },
                },
            },
            { type: 'card', settings: { caption: '@t:section.image_rail_caption_2' } },
            { type: 'card', settings: { caption: '@t:section.image_rail_caption_3' } },
            {
                type: 'card',
                settings: {
                    caption: '@t:section.image_rail_caption_4',
                    backgroundColor: { ref: 'custom', value: '#111111' },
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const imageRailMeta = {
    label: 'Image and video rail',
    group: 'Content',
    icon: 'grid',
};
//# sourceMappingURL=schema.js.map